import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Search, User } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import useAvatar from '../hooks/useAvatar';
import { useNotifications } from './alerts/useNotifications';
import AlertsDropdown from './alerts/AlertsDropdown';
import Badge from './ui/Badge';

interface PageHeaderProps {
  title: string;
  count?: number;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}

export default function PageHeader({
  title,
  count,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  actions,
  children,
}: PageHeaderProps) {
  const avatarUrl = useAvatar();
  const { notifications, unreadCount, loading: alertsLoading, markAsRead, markAllAsRead } = useNotifications();
  const [alertsOpen, setAlertsOpen] = useState(false);
  const alertsButtonRef = useRef<HTMLDivElement>(null);

  // Close alerts dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (alertsButtonRef.current && !alertsButtonRef.current.contains(e.target as Node)) {
        setAlertsOpen(false);
      }
    }
    if (alertsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [alertsOpen]);

  // "/" focuses the page search
  useEffect(() => {
    if (!onSearchChange) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName || '').toUpperCase();
      if (e.key === '/' && !/INPUT|TEXTAREA|SELECT/.test(tag)) {
        e.preventDefault();
        const el = document.querySelector<HTMLInputElement>('#ck-search-wrap input');
        if (el) el.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onSearchChange]);

  return (
    <header
      className="flex flex-col md:flex-row md:items-center justify-between gap-3"
      style={{
        padding: 'var(--space-5) 0',
        borderBottom: '1px solid var(--border-hairline)',
        marginBottom: 'var(--space-6)',
      }}
    >
      {/* Left: Title + count + inline actions */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px', minWidth: 0 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
            letterSpacing: 'var(--tracking-tight)',
            color: 'var(--text-body)',
          }}
        >
          {title}
        </h1>
        {count !== undefined && <Badge tone="neutral" count={count} />}
        {actions}
      </div>

      {/* Right: Live + Search + Theme + Alerts + Avatar */}
      <div className="flex items-center justify-end gap-2.5 w-full md:w-auto">
        <span className="ck-live hidden lg:inline-flex" title="Realtime sync active">
          <span className="pulse-dot pulse-dot--up is-pulsing"></span>
          <span className="t-eyebrow" style={{ color: 'var(--text-faint)' }}>Live</span>
        </span>

        {searchPlaceholder && onSearchChange && (
          <div id="ck-search-wrap" className="flex-1 md:flex-none md:w-[220px]" style={{ position: 'relative', minWidth: 0 }}>
            <Search
              size={15}
              style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-faint)', pointerEvents: 'none' }}
            />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchValue ?? ''}
              onChange={(e) => onSearchChange(e.target.value)}
              style={{
                width: '100%',
                height: 'var(--control-sm)',
                padding: '0 12px 0 34px',
                background: 'var(--surface-input)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-body)',
                outline: 'none',
                transition: 'border-color var(--dur-base) var(--ease-out), background var(--dur-base) var(--ease-out)',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--border-focus)';
                e.target.style.background = 'var(--surface-raised)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border-hairline)';
                e.target.style.background = 'var(--surface-input)';
              }}
            />
          </div>
        )}

        {children}

        <div className="hidden md:flex items-center" style={{ gap: '10px' }}>
          <ThemeToggle />

          {/* Alerts bell with live notifications */}
          <div ref={alertsButtonRef} className="relative">
            <button
              onClick={() => setAlertsOpen((prev) => !prev)}
              className="ck-iconbtn pressable hover-wash"
              aria-label="Alerts"
              style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'var(--control-md)',
                height: 'var(--control-md)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid transparent',
                background: alertsOpen ? 'var(--surface-active)' : 'transparent',
                color: 'var(--text-muted)',
                cursor: 'pointer',
              }}
            >
              <Bell size={16} />
              {unreadCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '6px',
                    right: '6px',
                    width: '7px',
                    height: '7px',
                    borderRadius: 'var(--radius-pill)',
                    background: 'var(--accent)',
                    boxShadow: '0 0 0 2px var(--surface-canvas)',
                  }}
                />
              )}
            </button>
            <AlertsDropdown
              isOpen={alertsOpen}
              onClose={() => setAlertsOpen(false)}
              notifications={notifications}
              unreadCount={unreadCount}
              loading={alertsLoading}
              markAsRead={markAsRead}
              markAllAsRead={markAllAsRead}
            />
          </div>

          {/* User avatar */}
          <Link
            to="/settings"
            className="pressable"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '30px',
              height: '30px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--border-hairline)',
              background: avatarUrl ? 'var(--surface-raised)' : 'var(--accent)',
              color: 'var(--text-on-accent)',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: 'var(--radius-pill)', objectFit: 'cover' }} />
            ) : (
              <User size={15} />
            )}
          </Link>
        </div>
      </div>
    </header>
  );
}
